/**
 * EN-4209: the orders worker flags a live fulfillment request Past SLA when its
 * provider sent no update by the handling deadline; the next provider update
 * clears it. The V2 orders API returns it as `past_sla` on list rows and the
 * detail. Legacy responses and older API builds leave it off, and the client
 * does not schema-parse responses, so anything unexpected reads as "not past SLA".
 */

export type PastSlaRequest = {
  request_id: string;
  connection_id: string;
  provider_node_id: string | null;
  /** ISO handling deadline; null when the worker flagged a request with none. */
  sla_at: string | null;
  /** ISO time the request was flagged. */
  past_sla_at: string;
};

export type PastSla = {
  /** ISO time of the earliest flag. */
  since: string;
  requests: PastSlaRequest[];
};

export const PAST_SLA_LABEL = 'Past SLA';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isIso = (value: unknown): value is string =>
  typeof value === 'string' &&
  ISO_DATE.test(value) &&
  !Number.isNaN(Date.parse(value));

function isRequest(value: unknown): value is PastSlaRequest {
  return (
    isRecord(value) &&
    typeof value.request_id === 'string' &&
    typeof value.connection_id === 'string' &&
    (value.provider_node_id === null ||
      typeof value.provider_node_id === 'string') &&
    (value.sla_at === null || isIso(value.sla_at)) &&
    isIso(value.past_sla_at)
  );
}

/** The order's Past SLA flag, or `undefined` when it has none or it is malformed. */
export function getPastSla(order: unknown): PastSla | undefined {
  const value = isRecord(order) ? order.past_sla : undefined;
  if (
    !isRecord(value) ||
    !isIso(value.since) ||
    !Array.isArray(value.requests) ||
    value.requests.length === 0 ||
    !value.requests.every(isRequest)
  ) {
    return undefined;
  }
  return { since: value.since, requests: value.requests };
}
